import { parseInventory } from "./inventory";
import { distanceBetween, getUserLocation, parseCoords } from "./user-location";

const SECTION_ID = "find-in-store";
const MODAL_ID = "find-in-store-modal";

// Matches the old theme's in-store "Only a few left!" cut-off, which is
// separate from the online Low stock threshold.
const STORE_LOW_STOCK_QUANTITY = 2;

const requests = new Map();

function loadDrawer(url) {
  if (requests.has(url)) return requests.get(url);

  const sectionUrl = new URL(url, window.location.origin);
  sectionUrl.searchParams.set("section_id", SECTION_ID);

  const request = fetch(sectionUrl).then((response) => {
    if (!response.ok) {
      throw new Error(`Find In-Store failed: ${response.status}`);
    }
    return response.text();
  });

  // Let a failed request be retried rather than cached.
  request.catch(() => requests.delete(url));
  requests.set(url, request);

  return request;
}

/**
 * Warms the cache, so the drawer usually opens on content that's arrived.
 *
 * @param {string} url Product URL
 */
export function prefetchFindInStore(url) {
  loadDrawer(url).catch(() => {});
}

/**
 * Opens the Find In-Store drawer on a product away from its product page,
 * such as from a product card. Waits for the content before opening: the
 * drawer's close button comes with it, and an empty drawer would leave focus
 * nowhere to go. A failed load goes to the product page instead.
 *
 * @param {HTMLElement} trigger Refocused when the drawer closes
 * @param {string} url Product URL
 * @param {string[]} [options] Option values to preselect, by option index
 */
export function openFindInStore(trigger, url, options) {
  if (trigger.hasAttribute("aria-busy")) return;

  trigger.setAttribute("aria-busy", "true");

  loadDrawer(url)
    .then((markup) => {
      const drawer = new DOMParser()
        .parseFromString(markup, "text/html")
        .querySelector("find-in-store");

      if (!drawer) throw new Error(`No Find In-Store markup for ${url}`);

      if (options) preselect(drawer, options);

      trigger.dispatchEvent(
        new CustomEvent("markup-modal:open", {
          bubbles: true,
          detail: { target: MODAL_ID, html: drawer.outerHTML },
        }),
      );
    })
    .catch(() => window.location.assign(url))
    .finally(() => trigger.removeAttribute("aria-busy"));
}

// Set as attributes, so the choice survives the trip through outerHTML.
function preselect(drawer, options) {
  drawer
    .querySelectorAll("select[data-find-in-store-option]")
    .forEach((select) => {
      const value = options[Number(select.dataset.findInStoreOption)];
      const option = [...select.options].find((o) => o.value === value);
      if (!option) return;

      select
        .querySelectorAll("option[selected]")
        .forEach((selected) => selected.removeAttribute("selected"));
      option.setAttribute("selected", "");
    });
}

/**
 * Drawer content for Find In-Store. Each store shows its stock for the chosen
 * variant, with stocked stores listed first. Once the shopper's location is
 * known, each group runs nearest first.
 *
 * On the product page its option selects stay in step with the product
 * form's. Opened from anywhere else, it carries its own selects, stock and
 * variant data (see find-in-store-drawer).
 */
class FindInStore extends HTMLElement {
  connectedCallback() {
    this.productForm = this.dataset.formId
      ? document.getElementById(this.dataset.formId)?.closest("product-form")
      : null;
    this.inventory = parseInventory(this.productForm || this);
    this.storeList = this.querySelector("[data-find-in-store-stores]");
    this.stores = [...this.storeList.children];
    this.productSelects = [
      ...(this.productForm?.querySelectorAll("select[data-option-index]") ||
        []),
    ];

    if (this.productForm) {
      this._createSelects();
      this.productForm.addEventListener(
        "variant:changed",
        this._onVariantChanged,
      );
    } else {
      this.selects = [
        ...this.querySelectorAll("select[data-find-in-store-option]"),
      ];
      this.addEventListener("change", this._onOwnChange);
    }

    this._render(this._currentSelection());
    this._sortByDistance();
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener(
      "variant:changed",
      this._onVariantChanged,
    );
    this.removeEventListener("change", this._onOwnChange);
  }

  _currentSelection() {
    return this.productForm
      ? this._productFormSelection()
      : this._ownSelection();
  }

  _ownSelection() {
    const { variants = [] } = window.scrapeSectionData(this);
    const pending = this.selects.find((select) => !select.value);

    // No choice to make: a single variant, or options with one value each
    if (!this.selects.length) {
      return variants[0] || null;
    }

    if (pending) return null;

    return (
      variants.find((variant) =>
        this.selects.every(
          (select) =>
            variant.options[Number(select.dataset.findInStoreOption)] ===
            select.value,
        ),
      ) || null
    );
  }

  _onOwnChange = (event) => {
    if (!event.target.matches("select[data-find-in-store-option]")) return;

    this._render(this._currentSelection());
  };

  _productFormSelection() {
    const id = this.productForm.querySelector("[data-product-form-id]")?.value;
    const { variants = [] } = window.scrapeSectionData(
      this.productForm.querySelector("product-form-options"),
    );

    return variants.find((variant) => String(variant.id) === id) || null;
  }

  _createSelects() {
    const container = this.querySelector("[data-find-in-store-options]");

    this.selects = this.productSelects.map((productSelect, index) => {
      const select = document.createElement("select");
      const label = document.createElement("label");
      const id = `FindInStore-${productSelect.id || index}`;

      select.id = id;
      select.className = "find-in-store__select";
      select.required = true;
      select.addEventListener("change", () => {
        productSelect.value = select.value;
        productSelect.dispatchEvent(new Event("change", { bubbles: true }));
      });

      label.className = "visually-hidden";
      label.htmlFor = id;
      label.textContent = productSelect.dataset.optionName;

      container.append(label, select);
      return select;
    });

    this._syncSelects();
  }

  // Copied rather than rendered so sold-out labels match the product form's,
  // which it rewrites on every change. Its fit-note option is left behind.
  _syncSelects() {
    this.selects.forEach((select, index) => {
      const productSelect = this.productSelects[index];
      const options = [...productSelect.options].filter(
        (option, optionIndex) => optionIndex === 0 || option.value,
      );

      select.replaceChildren(
        ...options.map((option) => option.cloneNode(true)),
      );
      select.value = productSelect.value;
    });
  }

  _onVariantChanged = (event) => {
    this._syncSelects();
    this._render(event.detail.variant);
  };

  _render(variant) {
    const quantities = variant ? this.inventory[variant.id] || {} : null;

    this.stores.forEach((store) => {
      const status = store.querySelector("[data-find-in-store-status]");

      // No variant chosen, or none for this combination
      if (!quantities) {
        status.hidden = true;
        delete store.dataset.stock;
        return;
      }

      const quantity = quantities[store.dataset.locationId] || 0;
      let stock = "none";
      if (quantity > STORE_LOW_STOCK_QUANTITY) stock = "in";
      else if (quantity > 0) stock = "low";

      store.dataset.stock = stock;
      status.hidden = false;
      status.querySelector("[data-find-in-store-status-text]").textContent =
        window.theme.translations(
          this.dataset,
          {
            in: "localeInStock",
            low: "localeLowStock",
            none: "localeUnavailable",
          }[stock],
        );

      const shipping = status.querySelector("[data-find-in-store-shipping]");
      if (shipping) shipping.hidden = stock === "none";
    });

    this._sortStores();
    this._renderContactLinks(variant?.sku || "");
    if (variant) this._renderPrice(variant);
  }

  async _sortByDistance() {
    const location = await getUserLocation(this.dataset.ipstackKey);
    if (!location || !this.isConnected) return;

    this.distances = new Map(
      this.stores.map((store) => {
        const coords = parseCoords(store.dataset.coords);
        return [store, coords ? distanceBetween(location, coords) : Infinity];
      }),
    );
    this._sortStores();
  }

  // Stores without coordinates, or before the location is known, keep their
  // metaobject order: sort is stable, and Infinity ties.
  _sortStores() {
    const stocked = (store) =>
      store.dataset.stock === "in" || store.dataset.stock === "low" ? 1 : 0;
    const distance = (store) => this.distances?.get(store) ?? Infinity;
    const byDistance = (a, b) =>
      distance(a) === distance(b) ? 0 : distance(a) - distance(b);

    this.storeList.append(
      ...[...this.stores].sort(
        (a, b) => stocked(b) - stocked(a) || byDistance(a, b),
      ),
    );
  }

  _renderPrice(variant) {
    const price = this.querySelector("[data-find-in-store-price]");
    if (!price) return;

    price.setAttribute("price", variant.price / 100);
    price.setAttribute("compare-at-price", variant.compare_at_price / 100);
    price.setAttribute("currency", window.Shopify.currency.active);
    price.setAttribute("dynamic", "");
  }

  _renderContactLinks(sku) {
    const subject = this._withSku(this.dataset.contactSubject, sku);
    const message = this._withSku(this.dataset.contactMessage, sku);

    this.querySelectorAll("[data-find-in-store-sms]").forEach((link) => {
      // `?&body=` is the form both iOS and Android read.
      link.href = `sms:${link.dataset.findInStoreSms}${
        message ? `?&body=${encodeURIComponent(message)}` : ""
      }`;
    });

    const params = [
      subject && `subject=${encodeURIComponent(subject)}`,
      message && `body=${encodeURIComponent(message)}`,
    ].filter(Boolean);

    this.querySelectorAll("[data-find-in-store-email]").forEach((link) => {
      link.href = `mailto:${link.dataset.findInStoreEmail}${
        params.length ? `?${params.join("&")}` : ""
      }`;
    });
  }

  // Without a SKU, the placeholder goes along with the separator before it.
  _withSku(text = "", sku) {
    return sku
      ? text.replaceAll("[sku]", sku)
      : text.replace(/\s*[-–—|:,]?\s*\[sku\]/g, "").trim();
  }
}

if (!customElements.get("find-in-store")) {
  customElements.define("find-in-store", FindInStore);
}
